import moment from "moment";

const currentDate = () => moment().format("MM/DD/YYYY");

/**
 * How long ago something happened, worded the same everywhere:
 * "45 secs", "1 min", "12 mins", "1 hour", "19 hours", "1 day", "4 days".
 * Shared by the notifications page and the header notification panel so the
 * same event never reads "19 hrs" in one place and "19 hours" in the other.
 */
export const formatRelativeTime = (eventDate: any): string => {
  const diffSeconds = Math.max(0, moment().diff(moment(eventDate), "seconds"));
  const unit = (value: number, name: string) =>
    `${value} ${name}${value === 1 ? "" : "s"}`;

  if (diffSeconds < 60) return unit(diffSeconds, "sec");
  if (diffSeconds < 3600) return unit(Math.floor(diffSeconds / 60), "min");
  if (diffSeconds < 86400) return unit(Math.floor(diffSeconds / 3600), "hour");
  return unit(Math.floor(diffSeconds / 86400), "day");
};

export const dateToString = (date: any) => {
  if (isMissingDate(date)) return EMPTY_DATE_PLACEHOLDER;
  const parsed = moment(date);
  if (!parsed.isValid()) return EMPTY_DATE_PLACEHOLDER;
  return parsed.format("MM/DD/YYYY");
};

const stringToDateObject = (dateString: any) => {
  const dateObject = moment(dateString, "MM/DD/YYYY").toDate();
  return dateObject;
};

const string1ToDateObject = (dateString: any) => {
  const dateObject = moment(dateString, "YYYY-MM-DD").toDate();
  return dateObject;
};

const addDayToDateObject = (date: any) => {
  const newDate = date;
  newDate.setDate(newDate.getDate() + 1);
  return newDate;
};

const trimLabel = (fileName: any, size: any) => {
  return fileName?.substring(0, size);
};

export const formatDateTimeZone = (timestamp: string, _timezone: string) => {
  const date = moment(timestamp);
  const isNegative = _timezone?.includes("-");
  let timezone = _timezone?.replace("+", "").replace("-", "");
  const timezoneArr = timezone?.split(".");
  const offsetHours = Number(timezoneArr?.[0]) || 0;
  const offsetMinutes = Number(timezoneArr?.[1]) || 0;
  let offsetMilliseconds = (offsetHours * 60 + offsetMinutes) * 60000;
  if (isNegative) offsetMilliseconds = -1 * offsetMilliseconds;

  const localDate = moment(date).add(offsetMilliseconds, "milliseconds");

  return localDate;
};

// Shown when there is no date to display. Callers used to guard for this
// themselves, which meant any caller that forgot rendered moment's literal
// "Invalid date" string to the customer.
export const EMPTY_DATE_PLACEHOLDER = "--";

const isMissingDate = (value: unknown) =>
  value === null || value === undefined || value === "";

/**
 * Global safety net, installed once at start-up. Anything moment cannot parse
 * formats as the placeholder instead of the literal "Invalid date", so no
 * un-migrated or future call site can leak that string to a customer.
 *
 * It cannot cover a *missing* value: moment(undefined) means "now", so an absent
 * date would silently format as today. Only an explicit check catches that,
 * which is why display code should call formatDisplayDate /
 * formatPaymentListDate rather than moment(...).format(...) directly.
 */
export const installSafeDateFormatting = () => {
  moment.updateLocale("en", { invalidDate: EMPTY_DATE_PLACEHOLDER });
};

/**
 * The helper to reach for when rendering any date to a customer. Returns the
 * placeholder for missing or unparseable values instead of a wrong-but-plausible
 * date.
 */
export const formatDisplayDate = (
  value: any,
  format: string = "MMM D, YYYY"
): string => {
  if (isMissingDate(value)) return EMPTY_DATE_PLACEHOLDER;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(format) : EMPTY_DATE_PLACEHOLDER;
};

/**
 * Card expiry for display, always as MM/YY. Stored values come in mixed shapes
 * ("7/28", "07/28", "07/2028"), so the same card list showed "7/28" next to
 * "07/38". Display only: the raw value is what the payment APIs expect.
 */
export const formatCardExpiry = (value: unknown): string => {
  if (isMissingDate(value)) return EMPTY_DATE_PLACEHOLDER;
  const match = String(value)
    .trim()
    .match(/^(\d{1,2})\s*\/\s*(\d{2}|\d{4})$/);
  if (!match) return String(value);
  const [, month, year] = match;
  return `${month.padStart(2, "0")}/${year.slice(-2)}`;
};

export const formatPaymentListDate = (
  utcTimestamp: any,
  timezoneOffset: string,
  formatType: "short" | "long",
) => {
  return formatDisplayDate(
    utcTimestamp,
    formatType === "short" ? "MMM D, YYYY" : "MMMM D, YYYY",
  );
};
// try {
//   // Check if the timestamp is missing the 'T' (local date format)
//   if(utcTimestamp){
//     if(!timezoneOffset){
//       return moment(utcTimestamp).format(formatType === "short" ? "MMM D, YYYY" : "MMMM D, YYYY")
//     }

//     if (!utcTimestamp.includes("T")) {
//       // If missing, add 'T' between date and time
//       utcTimestamp = utcTimestamp.replace(" ", "T");
//     }

//     const date = new Date(utcTimestamp);

//     // Check if the date is valid
//     if (isNaN(date.getTime())) {
//       console.error("Invalid date:", utcTimestamp);
//       return formatDateTimeZone(utcTimestamp, timezoneOffset).format(
//         formatType === "short" ? "MMM D, YYYY" : "MMMM D, YYYY"
//       );
//     }

//     // Convert timezoneOffset to minutes and adjust the UTC minutes
//     const offsetMinutes = parseFloat(timezoneOffset) * 60;
//     const utcMinutes = date.getUTCMinutes() + offsetMinutes;
//     date.setUTCMinutes(utcMinutes);

//     let hours = date.getUTCHours();
//     let minutes = date.getUTCMinutes();
//     const day = date.getUTCDate();
//     const month = date.toLocaleString("en-US", {
//       month: formatType,
//       timeZone: "UTC",
//     });
//     const year = date.getUTCFullYear();

//     // Determine AM/PM period
//     const period = hours >= 12 ? "PM" : "AM";
//     hours = hours % 12;
//     hours = hours ? hours : 12; // Handle the case where hour is 0 (12 AM)
//     const formattedMinutes = minutes < 10 ? "0" + minutes : minutes.toString();

//     const currYear = new Date().getUTCFullYear();

//     // Format date
//     return `${month} ${day}, ${year}`;
//   }else{
//     return null
//   }
// } catch (err) {
//   console.error("Error formatting date:", err);
//   return formatDateTimeZone(utcTimestamp, timezoneOffset).format(
//     formatType === "short" ? "MMM D, YYYY" : "MMMM D, YYYY"
//   );
// }
